import { Component, HostBinding, inject } from '@angular/core';
import {ApiHomeRow, ApiTopicItem} from '@tytapp/api';
import { ContentBlockViewComponent, ContentModule } from '@tytapp/content';
import { TopicsService } from '@tytapp/topics';
import { MediaUiModule } from '../media-ui.module';
import { FollowedTopicBlock } from './followed-topic-block';
import {ChatModule} from '@tytapp/chat';
import { ArticlesService } from '@tytapp/articles';
import { ProductionsService } from '@tytapp/common-ui';

@Component({
    selector: 'tyt-followed-topic-block',
    standalone: true,
    imports: [ContentModule, MediaUiModule, ChatModule],
    template: `
        @if (topics) {
            @if (block.layout === 'list') {
                <div class="list">
                    @for (topic of topics; track topic; let i = $index) {
                        <div class="topic-group">
                            <div class="topic-title">{{ topic.topic.name }}</div>
                            @for (item of topic.topic_items; track item; let j = $index) {
                                <a [routerLink]="linkForTopicItem(item)" class="item">
                                    <div class="logo" role="img" [responsiveBackgroundImage]="item.brand_logo" [attr.aria-label]="titleForTopic(topic, j)"></div>
                                    <div class="description">
                                        <div class="title">{{item.title | summarize}}</div>
                                        <div class="date">{{ item.date | date: 'longDate' }}</div>
                                        <div class="details">
                                            <span class="badge">Clip</span>
                                            @if (item.duration && item.duration > 0) {
                                                <span class="duration">{{ item.duration | duration }}</span>
                                            }
                                            @if (commentTopicForTopicItem(item)) {
                                                <span class="comments">
                                                    <tyt-comments-count [identifier]="commentTopicForTopicItem(item)"></tyt-comments-count>
                                                </span>
                                            }
                                        </div>
                                    </div>
                                </a>
                            }
                        </div>
                    }
                </div>
            } @else if (block.layout === 'thumbnail') {
                @for (topic of topics; track topic) {
                    <tyt-topic-card
                        [topic]="topic"
                        [title]="titleForTopic(topic, $index)"
                    />
                }
            }
        } @else {
            <!-- TODO: placeholder -->
            <app-spinner></app-spinner>
        }
    `,
    styles: [`
        .list {
            display: flex;
            flex-direction: column;
        }

        .item {
            display: flex;
            align-items: flex-start;
            border: 1px solid #252a37;
            margin: 1em 0;
            padding: 1em;
            color: inherit;
        }

        .logo {
            width: 50px;
            height: 50px;
            background-size: cover;
            background-position: center;
            margin-right: 1em;
            min-width: 50px;
            min-height: 50px;
        }

        .description {
            display: flex;
            flex-direction: column;
        }

        .title {
            font-weight: bold;
        }

        .date {
            color: #777;
        }

        .details {
            display: flex;
            gap: 1em;
            align-items: center;
            color: #777;
        }

        .badge {
            background: blue;
            color: white;
            padding: 0px 14px;
        }

        .duration, .comments {
            font-size: 90%;
        }
    `]
})
export class ViewComponent extends ContentBlockViewComponent<FollowedTopicBlock> {
    private topicsService = inject(TopicsService);
    private articlesService = inject(ArticlesService);
    private productionsService = inject(ProductionsService);

    @HostBinding('class.animate-in')
    animatedIn = false;

    async onBlockChanged() {
        this.block.layout ??= 'thumbnail'; // set default layout if undefined
        let topics = await this.topicsService.followed();
        this.topics = topics.slice(
            (this.block.start ?? 1) - 1,
            this.block.count ? (this.block.start ?? 1) - 1 + this.block.count : undefined
        );
        setTimeout(() => this.animatedIn = true, 100);
    }

    titleForTopic(topic: ApiHomeRow, index: number) {
        if (!topic)
            return undefined;

        if (this.inEditor) {
            return `${topic.topic.name} (Topic #${this.block.start + index})`;
        }

        return topic.topic.name;
    }

    linkForTopicItem(topicItem: ApiTopicItem) {
        if (topicItem.type === 'vod') {
            return `/watch/${topicItem.brand_id}/${topicItem.production_id}/${topicItem.vod_type}s/${topicItem.vod_id}`;
        } else if (topicItem.type === 'article') {
            return this.articlesService.getArticleUrlFromTopicItemSync(topicItem);
        }

        // uh oh
        return `javascript:;`;
    }

    commentTopicForTopicItem(topicItem: ApiTopicItem) {
        if (topicItem.type === 'vod')
            return `vod_${topicItem.vod_id}`;
        if (topicItem.type === 'article')
            return `article_${topicItem.article_id}`;

        return null;
    }

    topics: ApiHomeRow[];
}
